import { z } from 'zod';
import { IceServerSchema, PublicConfigSchema } from './config.js';

/**
 * Wire protocol. JSON over the control channel (DataChannel or WebSocket)
 * and the signaling WebSocket. Deliberately language-agnostic: any future
 * non-TS peer only needs to speak these JSON shapes.
 */

export const PROTOCOL_VERSION = 1;

const rate = z.number().min(-1).max(1);

/**
 * Complete input state, sent at a fixed tick rate. Snapshots, not deltas:
 * a lost frame is fully superseded by the next one.
 */
export const ControlFrameSchema = z.object({
  v: z.literal(PROTOCOL_VERSION),
  type: z.literal('control'),
  /** Monotonic per-session sequence. The agent drops non-newer frames. */
  seq: z.number().int().nonnegative(),
  /** Client wall-clock ms, echoed in the ack for RTT measurement. */
  t: z.number(),
  /** Latched emergency stop. While true the agent ignores motion input. */
  estop: z.boolean(),
  drive: z.object({ left: rate, right: rate }),
  slew: rate,
  arm: z.object({ boom: rate, dipper: rate, bucket: rate }),
});
export type ControlFrame = z.infer<typeof ControlFrameSchema>;

/**
 * Dead-reckoned pose estimate, integrated agent-side from the rates the
 * actuator was actually given (so it reflects deadman stops and E-STOP).
 * Open-loop — it drifts. When real sensors land, the agent swaps the
 * integrator for measurements and this wire shape stays identical.
 */
export const DiggerPoseSchema = z.object({
  /** Track-base heading, degrees clockwise from the power-on direction. */
  headingDeg: z.number(),
  /** Cab rotation relative to the tracks, degrees clockwise, wrapped ±180. */
  slewDeg: z.number(),
  /** Boom elevation, 0 = horizontal, positive = up. */
  boomDeg: z.number(),
  /** Dipper angle relative to the boom axis. */
  dipperDeg: z.number(),
  /** Bucket angle relative to the dipper axis. */
  bucketDeg: z.number(),
});
export type DiggerPose = z.infer<typeof DiggerPoseSchema>;

/** Agent → client, one per applied control frame. */
export const ControlAckSchema = z.object({
  v: z.literal(PROTOCOL_VERSION),
  type: z.literal('ack'),
  seq: z.number().int().nonnegative(),
  /** Echo of ControlFrame.t — client computes RTT without clock sync. */
  tClient: z.number(),
  /** Agent wall-clock ms when the frame was applied. */
  tAgent: z.number(),
  /** Agent-side safety state, so the HUD reflects the authoritative truth. */
  estopped: z.boolean(),
  deadmanTripped: z.boolean(),
  /** Present when the agent has a pose source (dead-reckoned or sensed). */
  pose: DiggerPoseSchema.optional(),
});
export type ControlAck = z.infer<typeof ControlAckSchema>;

export const ControlMessageSchema = z.discriminatedUnion('type', [
  ControlFrameSchema,
  ControlAckSchema,
]);
export type ControlMessage = z.infer<typeof ControlMessageSchema>;

/**
 * Pi system telemetry, served at GET /api/system and polled by the console.
 * Fields the platform can't measure are null (e.g. no vcgencmd on a dev
 * Mac, no PMIC power rails before the Pi 5).
 */
export const SystemStatsSchema = z.object({
  /** Agent wall-clock ms when sampled. */
  t: z.number(),
  uptimeSec: z.number(),
  cpu: z.object({
    /** Overall busy percentage since the previous sample; null on first. */
    percent: z.number().nullable(),
    loadAvg1: z.number(),
    cores: z.number(),
    clockMhz: z.number().nullable(),
    tempC: z.number().nullable(),
  }),
  memory: z.object({ usedMb: z.number(), totalMb: z.number() }),
  power: z.object({
    /** Total draw summed from the Pi 5 PMIC rails; null when unavailable. */
    watts: z.number().nullable(),
    /** vcgencmd get_throttled flags — brownout on a battery digger is an
     *  operator-visible event, not a log line. */
    throttled: z
      .object({
        undervoltageNow: z.boolean(),
        cappedNow: z.boolean(),
        throttledNow: z.boolean(),
        softTempLimitNow: z.boolean(),
        undervoltageEver: z.boolean(),
        throttledEver: z.boolean(),
      })
      .nullable(),
  }),
});
export type SystemStats = z.infer<typeof SystemStatsSchema>;

// ---------------------------------------------------------------------------
// Signaling (WebSocket). On LAN the agent hosts this; on the internet the
// same messages relay through the signaling server, which forwards
// offer/answer/ice/config verbatim (it never inspects SDP) and adds its own
// envelope messages: agent-hello[-ok], peer-joined/peer-left, ice-servers.
// ---------------------------------------------------------------------------

export const CameraName = z.enum(['front', 'rear']);
export type CameraName = z.infer<typeof CameraName>;

export const SignalHelloSchema = z.object({
  type: z.literal('hello'),
  role: z.literal('operator'),
  /** Auth token: short-lived JWT (relay path) or static token (LAN). */
  token: z.string().optional(),
  /** Which digger to reach through the relay; relay default when unset. */
  diggerId: z.string().optional(),
});

export const SignalOfferSchema = z.object({
  type: z.literal('offer'),
  sdp: z.string(),
  /** SDP mid → camera identity, so the console never guesses track order. */
  cameras: z.record(z.string(), CameraName),
});

export const SignalAnswerSchema = z.object({
  type: z.literal('answer'),
  sdp: z.string(),
});

export const SignalIceSchema = z.object({
  type: z.literal('ice'),
  candidate: z.object({
    candidate: z.string(),
    sdpMid: z.string().nullish(),
    sdpMLineIndex: z.number().nullish(),
  }),
});

export const SignalByeSchema = z.object({
  type: z.literal('bye'),
  reason: z.string(),
});

export const SignalErrorSchema = z.object({
  type: z.literal('error'),
  message: z.string(),
});

/** Agent → relay, first message on the dial-out link. */
export const SignalAgentHelloSchema = z.object({
  type: z.literal('agent-hello'),
  diggerId: z.string(),
  agentKey: z.string(),
});

/** Relay → agent: registration accepted (resets the agent's backoff). */
export const SignalAgentHelloOkSchema = z.object({
  type: z.literal('agent-hello-ok'),
});

/**
 * Relay → agent: an operator authenticated; their `hello` follows verbatim.
 * Sent first because werift needs the (minted, agent-flavored) ICE servers
 * before the PeerConnection is constructed.
 */
export const SignalPeerJoinedSchema = z.object({
  type: z.literal('peer-joined'),
  iceServers: z.array(IceServerSchema),
});

/** Relay → agent: the operator's socket dropped — stop and tear down. */
export const SignalPeerLeftSchema = z.object({
  type: z.literal('peer-left'),
  reason: z.string(),
});

/** Relay → operator: minted ICE servers (browser flavor, may incl. turns:). */
export const SignalIceServersSchema = z.object({
  type: z.literal('ice-servers'),
  iceServers: z.array(IceServerSchema),
});

/**
 * Agent → operator, after the token check and before the offer: the console
 * behind the relay can't reach /api/config on the Pi, so config rides the
 * signaling channel in every mode (one code path).
 */
export const SignalConfigSchema = z.object({
  type: z.literal('config'),
  config: PublicConfigSchema,
});

/** Agent → operator: periodic Pi telemetry (replaces /api/system polling). */
export const SignalSystemSchema = z.object({
  type: z.literal('system'),
  stats: SystemStatsSchema,
});

/** Operator → agent: zero the dead-reckoned pose (replaces POST /api/pose/reset). */
export const SignalPoseResetSchema = z.object({
  type: z.literal('pose-reset'),
});

export const SignalMessageSchema = z.discriminatedUnion('type', [
  SignalHelloSchema,
  SignalOfferSchema,
  SignalAnswerSchema,
  SignalIceSchema,
  SignalByeSchema,
  SignalErrorSchema,
  SignalAgentHelloSchema,
  SignalAgentHelloOkSchema,
  SignalPeerJoinedSchema,
  SignalPeerLeftSchema,
  SignalIceServersSchema,
  SignalConfigSchema,
  SignalSystemSchema,
  SignalPoseResetSchema,
]);
export type SignalMessage = z.infer<typeof SignalMessageSchema>;

/** Parse a JSON wire string; returns undefined on any malformed input. */
export function parseControlMessage(raw: string): ControlMessage | undefined {
  return safeParse(ControlMessageSchema, raw);
}

export function parseSignalMessage(raw: string): SignalMessage | undefined {
  return safeParse(SignalMessageSchema, raw);
}

function safeParse<T>(schema: z.ZodType<T>, raw: string): T | undefined {
  let json: unknown;
  try {
    json = JSON.parse(raw);
  } catch {
    return undefined;
  }
  const result = schema.safeParse(json);
  return result.success ? result.data : undefined;
}
